import type { CockpitTask } from '@shared/definitions/action-card.model';

/**
 * Normalize an unknown date value to milliseconds since epoch, regardless of
 * whether Firestore handed back a `Timestamp` instance, a serialized
 * `{seconds, nanoseconds}` object, an ISO string, a JS `Date`, or `null`.
 *
 * Returns `Number.POSITIVE_INFINITY` for missing/unparseable dates so an
 * ascending sort comparator pushes them to the end of the list.
 *
 * Kept in its own module so the firestore store, the mock store, the dashboard
 * widget and the cockpit calendar share the same lenient parsing — without it,
 * the mock seed (ISO strings) sorted differently from real Firestore docs
 * (Timestamp objects) and the Agenda widget rendered in different orders.
 */
export function toMillis(v: unknown): number {
  if (v == null) return Number.POSITIVE_INFINITY;
  if (typeof v === 'string') {
    const ms = Date.parse(v);
    return Number.isNaN(ms) ? Number.POSITIVE_INFINITY : ms;
  }
  if (v instanceof Date) return v.getTime();
  // Firestore Timestamp instance (SDK) — has `toMillis()`.
  if (typeof (v as { toMillis?: () => number }).toMillis === 'function') {
    return (v as { toMillis: () => number }).toMillis();
  }
  // Serialized timestamp shape `{seconds, nanoseconds}`.
  const obj = v as { seconds?: unknown; nanoseconds?: unknown };
  if (typeof obj.seconds === 'number') {
    const nano = typeof obj.nanoseconds === 'number' ? obj.nanoseconds : 0;
    return obj.seconds * 1000 + Math.floor(nano / 1_000_000);
  }
  return Number.POSITIVE_INFINITY;
}

/** {@link toMillis} applied to a task's `dueDate`. */
export function dueDateToMillis(task: Pick<CockpitTask, 'dueDate'>): number {
  return toMillis(task.dueDate);
}

/** Format a task's `dueDate` as a short locale date, or `'—'` when missing. */
export function formatDueDate(task: Pick<CockpitTask, 'dueDate'>, locale?: string): string {
  const ms = dueDateToMillis(task);
  if (!Number.isFinite(ms)) return '—';
  return new Date(ms).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
}
